import React, { useState, useEffect, useCallback } from 'react';
import {

  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  TextField,
  IconButton,
  useTheme,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  FormControlLabel,
  Slide,
  useMediaQuery,
} from '@mui/material';
import {
  Close,
  Warning,
  Delete,
  Movie,
  Star,
  Timeline,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import { User } from 'firebase/auth';

import { firebaseService } from '../../services/firebaseService';
import WatchlistsIcon from '../widgets/WatchlistsIcon';
import CustomButton from '../widgets/CustomButton';
import BottomSheet, { BottomSheetDraggableHeader } from './BottomSheet';
import { font } from '../../utils/fontHelper';
import { glassStyles } from '../../tokens/designTokens';
import { useKeyboardAware } from '../../hooks/useKeyboardAware';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface DeleteAccountDialogProps {
  open: boolean;
  onClose: () => void;
  user?: User;
  onAccountDeleted?: () => void;
}

interface UserDataSummary {
  watchlistsCount: number;
  moviesCount: number;
  reviewsCount: number;
  activitiesCount: number;
}

const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({
  open,
  onClose,
  user,
  onAccountDeleted,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isKeyboardOpen, keyboardHeight, bottomPadding, scrollIntoViewOnFocus } = useKeyboardAware();
  const [step, setStep] = useState<'warning' | 'confirmation' | 'password' | 'deleting'>('warning');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [userDataSummary, setUserDataSummary] = useState<UserDataSummary | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const isPasswordProvider = user?.providerData?.some(provider => provider.providerId === 'password') ?? false;
  const requiredText = 'DELETE MY ACCOUNT';

  const loadUserDataSummary = useCallback(async () => {
    if (!user) return;

    setLoadingData(true);
    try {
      const summary = await firebaseService.getUserDataSummary(user.uid);
      setUserDataSummary(summary);
    } catch (error) {
      console.error('Error loading user data summary:', error);
      setError('Failed to load account data summary');
    } finally {
      setLoadingData(false);
    }
  }, [user]);

  useEffect(() => {
    if (open && user && step === 'warning') {
      loadUserDataSummary();
    }
  }, [open, user, step, loadUserDataSummary]);

  const handleNext = () => {
    if (step === 'warning') {
      setStep('confirmation');
    } else if (step === 'confirmation') {
      if (isPasswordProvider) {
        setStep('password');
      } else {
        handleDeleteAccount();
      }
    } else if (step === 'password') {
      handleDeleteAccount();
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    setStep('deleting');
    setError(null);

    try {
      // Check if this is a password provider account
      const needsPassword = firebaseService.isPasswordProvider(user);

      if (needsPassword && !password.trim()) {
        setError('Password is required for account deletion.');
        setStep('password');
        return;
      }

      await firebaseService.deleteUserAccount(user, needsPassword ? password : undefined);

      // Clear any local storage
      localStorage.clear();
      sessionStorage.clear();

      if (onAccountDeleted) {
        onAccountDeleted();
      }
    } catch (error: any) {
      console.error('Error deleting account:', error);

      // Handle specific Firebase Auth errors
      let errorMessage = 'Failed to delete account. Please try again.';

      if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
        setStep('password');
      } else if (error.code === 'auth/requires-recent-login') {
        if (isPasswordProvider) {
          errorMessage = 'For security reasons, please enter your password to confirm account deletion.';
          setStep('password');
        } else {
          errorMessage = 'For security reasons, please sign out and sign back in, then try deleting your account again.';
          setStep('confirmation');
        }
      } else if (error.code === 'auth/user-token-expired') {
        errorMessage = 'Your session has expired. Please sign out and sign back in, then try again.';
        setStep('confirmation');
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please wait a few minutes and try again.';
        setStep(isPasswordProvider ? 'password' : 'confirmation');
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection and try again.';
        setStep(isPasswordProvider ? 'password' : 'confirmation');
      } else if (error.message === 'Recent authentication required') {
        // Our custom enhanced error
        if (isPasswordProvider) {
          errorMessage = 'For security reasons, please enter your password to confirm account deletion.';
          setStep('password');
        } else {
          errorMessage = 'For security reasons, please sign out and sign back in, then try deleting your account again.';
          setStep('confirmation');
        }
      }

      setError(errorMessage);
    }
  };

  const handleClose = () => {
    if (step !== 'deleting') {
      setStep('warning');
      setPassword('');
      setConfirmationText('');
      setAcknowledged(false);
      setError(null);
      onClose();
    }
  };

  const isNextDisabled = () => {
    if (step === 'warning') {
      return !acknowledged || loadingData;
    } else if (step === 'confirmation') {
      return confirmationText !== requiredText;
    } else if (step === 'password') {
      return !password.trim();
    }
    return false;
  };

  const renderStepContent = () => {
    switch (step) {
      case 'warning':
        return (
          <Box>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                This action cannot be undone!
              </Typography>
              <Typography variant="body2">
                Deleting your account will permanently remove all your data from our servers.
              </Typography>
            </Alert>

            {loadingData ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Loading your account data...
                </Typography>
              </Box>
            ) : userDataSummary ? (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Your account contains:
                </Typography>

                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <WatchlistsIcon size={24} />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${userDataSummary.watchlistsCount} watchlists`}
                      secondary="Custom movie lists you've created"
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <Movie />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${userDataSummary.moviesCount} movie interactions`}
                      secondary="Movies you've added to watchlist or marked as watched"
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <Star />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${userDataSummary.reviewsCount} reviews`}
                      secondary="Movie reviews and ratings you've written"
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <Timeline />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${userDataSummary.activitiesCount} activity entries`}
                      secondary="Your movie watching history and activities"
                    />
                  </ListItem>
                </List>
              </Box>
            ) : null}

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                When you delete your account, we will:
              </Typography>

              <List dense sx={{
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                borderRadius: 1,
                p: 1
              }}>
                <ListItem sx={{ py: 0.5 }}>
                  <Typography variant="body2">• Delete all your watchlists and movie data</Typography>
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <Typography variant="body2">• Remove all your reviews and ratings</Typography>
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <Typography variant="body2">• Delete your activity history</Typography>
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <Typography variant="body2">• Remove you from any shared watchlists</Typography>
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <Typography variant="body2">• Delete your Firebase authentication account</Typography>
                </ListItem>
              </List>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  color="error"
                />
              }
              label={
                <Typography variant="body2">
                  I understand this action is permanent and cannot be undone
                </Typography>
              }
            />
          </Box>
        );

      case 'confirmation':
        return (
          <Box>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Final Confirmation Required
              </Typography>
              <Typography variant="body2">
                To confirm deletion, please type <strong>"{requiredText}"</strong> below:
              </Typography>
            </Alert>

            <TextField
              fullWidth
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder={requiredText}
              variant="outlined"
              onFocus={scrollIntoViewOnFocus}
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: 2,
                  '& fieldset': { borderColor: 'transparent' },
                  '&:hover fieldset': { borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' },
                  '&.Mui-focused fieldset': { 
                    borderColor: theme.palette.primary.main,
                    borderWidth: '2px'
                  },
                }
              }}
              error={confirmationText.length > 0 && confirmationText !== requiredText}
              helperText={
                confirmationText.length > 0 && confirmationText !== requiredText
                  ? 'Text must match exactly'
                  : ''
              }
            />

            <Typography variant="body2" color="text.secondary">
              This confirmation helps ensure you really want to delete your account.
            </Typography>
          </Box>
        );

      case 'password':
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Security Verification Required
              </Typography>
              <Typography variant="body2">
                For your protection, Firebase requires recent authentication before permanently deleting your account.
                Please enter your current password to confirm your identity.
              </Typography>
            </Alert>

            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your current password"
              variant="outlined"
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: 2,
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && password.trim() && !isNextDisabled()) {
                  handleNext();
                }
              }}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              This is the same password you use to sign in to your account.
            </Typography>
          </Box>
        );

      case 'deleting':
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <LinearProgress sx={{ mb: 3 }} />
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Deleting Your Account...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we remove all your data from our servers.
              This may take a few moments.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 'warning':
        return 'Delete Account';
      case 'confirmation':
        return 'Confirm Deletion';
      case 'password':
        return 'Enter Password';
      case 'deleting':
        return 'Deleting Account';
      default:
        return 'Delete Account';
    }
  };

  const dialogContent = (
    <>
      <BottomSheetDraggableHeader sx={{
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.text.primary,
        fontSize: 18,
        fontWeight: 600,
        fontFamily: font(),
        pt: isMobile ? 2.5 : 2,
        pl: 3,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning sx={{ color: '#FF3B30', fontSize: 24 }} />
          {getStepTitle()}
        </Box>
      </BottomSheetDraggableHeader>

      <IconButton
        onClick={handleClose}
        disabled={step === 'deleting'}
        size="small"
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: theme.palette.text.primary,
          zIndex: 1,
          '&:hover': {
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
          },
        }}
      >
        <Close />
      </IconButton>

      <DialogContent sx={{
        p: 3,
        pb: isMobile ? Math.max(3, parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sait') || '0') + 3) : 3,
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{
        p: 3,
        pt: 1,
        pb: isMobile ? Math.max(3, parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sait') || '0') + 3) : 3,
      }}>
        {step !== 'deleting' && (
          <>
            <CustomButton
              variant="outlined"
              semantic="secondary"
              onClick={handleClose}
              label="Cancel"
            />
            <CustomButton
              semantic="error"
              onClick={handleNext}
              disabled={isNextDisabled()}
              icon={step === 'confirmation' || step === 'password' ? <Delete /> : undefined}
              label={step === 'warning' ? 'Continue' :
                step === 'confirmation' ? 'Delete Account' :
                  step === 'password' ? 'Delete Account' : 'Delete'}
            />
          </>
        )}
      </DialogActions>
    </>
  );

  return isMobile ? (
    <BottomSheet
      open={open}
      onClose={handleClose}
      maxHeight="90vh"
      bottom={isKeyboardOpen ? keyboardHeight : 0}
    >
      {dialogContent}
    </BottomSheet>
  ) : (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          ...glassStyles.popup(theme.palette.mode === 'dark'),
        }
      }}
      TransitionComponent={Transition}
    >
      {dialogContent}
    </Dialog>
  );
};

export default DeleteAccountDialog; 